import React, { useState, useEffect, useMemo } from "react";
import { useProjects } from "./useProjects";
import styles from "./ProjectsPage.module.css";
import classNames from "classnames";

import ProjectsPageCollapsible from "./ProjectsPageCollapsible";
import ProjectItem from "../ProjectItem";

import Select from "../Shared/Select";
import { Header } from "../Shared/Header/Header";
import { TabBar } from "../Shared/TabBar/TabBar";
import projectsIcon from "../assets/Menu_Icon_Projects.svg";
import Auth from "shared/@auth";
import { useIsFetching } from "react-query";
import Loader from "shared/Loader";

/**
 * View component that displays the Projects Page
 */

export function ProjectsPage({ meetingType }) {
  const { hasPermission } = Auth.useContainer();
  const readOnly = !hasPermission("management-team.admins");
  const _ = require("lodash");

  const {
    projects,
    notePost,
    archiveProjects,
    notePut,
    noteDelete,
    statusPut,
    fetchFile,
    currentRefetch,
    archiveRefetch,
    currentStatus,
    archiveStatus,
    _location,
  } = useProjects();

  const [activeTitle, setActiveTitle] = useState("Current Projects");
  const [year, setYear] = useState("");
  const [selectedOption, setSelectedOption] = useState("");
  const [optionList, setOptionList] = useState([]);
  const [projectsArray, setProjectsArray] = useState([]);
  const [archiveProjectsArray, setArchiveProjectsArray] = useState([]);
  const [openIndex, setOpenIndex] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Track background fetches for accurate spinner behavior
  const fetchingCurrent = useIsFetching(["projects", _location]);
  const fetchingArchive = useIsFetching(["archiveProjects", _location]);

  useMemo(() => {
    if (projects !== undefined)
      setProjectsArray(
        projects.sort(function compare(a, b) {
          var dateA = new Date(a.submittedOn);
          var dateB = new Date(b.submittedOn);
          return dateB - dateA;
        })
      );
  }, [projects]);

  useMemo(() => {
    if (archiveProjects !== undefined)
      setArchiveProjectsArray(
        archiveProjects.sort(function compare(a, b) {
          var dateA = new Date(a.submittedOn);
          var dateB = new Date(b.submittedOn);
          return dateB - dateA;
        })
      );
  }, [archiveProjects]);

  const [organizedArchiveData, setOrganizedArchiveData] = useState(null);

  // group archive data by date
  useMemo(() => {
    setOrganizedArchiveData(
      _.groupBy(archiveProjectsArray, function (project) {
        return project.submittedOn.substring(0, 7);
      })
    );
  }, [archiveProjectsArray, _]);

  const uniqueYears = _.uniq(
    _.map(archiveProjects, function (project) {
      return parseInt(project.submittedOn.substring(0, 4));
    })
  );

  // defaults initial selection to most recent archived entry
  const initialValue = Math.max(...uniqueYears).toString();

  // sorts option list in descending order
  const options = uniqueYears.map(String).sort((a, b) => {
    return b - a;
  });

  useEffect(() => {
    setYear(initialValue);
    setSelectedOption(initialValue);
    setOptionList(options);
  }, [initialValue]);

  const months = {
    January: "01",
    February: "02",
    March: "03",
    April: "04",
    May: "05",
    June: "06",
    July: "07",
    August: "08",
    September: "09",
    October: "10",
    November: "11",
    December: "12",
  };

  const onNoteSave = (_id, note) => {
    notePost.mutate({
      projectId: _id,
      note: note,
    });
  };

  const onNoteEdit = (_id, noteId, e) => {
    notePut.mutate({
      projectId: _id,
      noteId: noteId,
      note: e,
    });
  };

  const onNoteDelete = (_id, noteId) => {
    noteDelete.mutate({
      projectId: _id,
      noteId: noteId,
    });
  };

  const onStatusChange = (_id, e) => {
    statusPut.mutate({
      _id: _id,
      status: e,
    });
  };

  const handleChange = (e) => {
    if (e !== "") {
      setYear(e);
      setSelectedOption(e);
    }
  };

  const onDownload = (e) => {
    if (e !== undefined && e.length > 0) {
      if (e.indexOf("https://") !== -1) {
        var image = e.match(/projects\/.*?$/g)[0];
        fetchFile(image.substring(9));
      } else {
        fetchFile(e);
      }
    }
  };

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      await Promise.all([currentRefetch(), archiveRefetch()]);
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div
      className={classNames(
        activeTitle === "Current Projects"
          ? styles.container
          : styles.archiveContainer
      )}
    >
      <div className={styles.header}>
        <Header
          title={"Projects"}
          icon={projectsIcon}
          onRefresh={handleRefresh}
          isLoading={isRefreshing}
          children={
            <div className={styles.tabBar}>
              <TabBar
                titleList={[
                  { title: "Current Projects", notification: 0 },
                  { title: "Archived Projects", notification: 0 },
                ]}
                activeTitle={activeTitle}
                onChange={(title) => setActiveTitle(title)}
              />
            </div>
          }
        />
      </div>
      {organizedArchiveData &&
        Object.keys(organizedArchiveData).length > 0 &&
        activeTitle === "Archived Projects" && (
          <div className={styles.yearSelect}>
            <Select
              placeholder={year}
              options={optionList}
              onChange={(e) => {
                setSelectedOption(e);
                handleChange(e);
              }}
              selectedOption={selectedOption}
              name="year"
            />
          </div>
        )}
      <div
        className={classNames(
          activeTitle === "Current Projects"
            ? styles.contentScrollable
            : styles.archiveScrollable
        )}
      >
        <div className={styles.content}>
          {activeTitle === "Current Projects" ? (
            <>
              {currentStatus === "loading" ? (
                <div className={styles.loading}>
                  <Loader />
                </div>
              ) : currentStatus === "error" ? (
                <div className={styles.loadMessage}>
                  Error loading projects. Please try again.
                </div>
              ) : projectsArray.length > 0 ? (
                <>
                  {projectsArray.map((project) => (
                    <div className={styles.projectItems} key={project._id}>
                      <ProjectItem
                        readOnly={readOnly}
                        date={`${Object.keys(months).find((name) => {
                          if (
                            months[name] === project.submittedOn.substring(5, 7)
                          ) {
                            return name;
                          }
                          return null;
                        })} ${project.submittedOn.substring(
                          8,
                          10
                        )}, ${project.submittedOn.substring(0, 4)}`}
                        status={project.status}
                        img={
                          project.files !== null &&
                          project.files.length > 0 &&
                          project.files[0].src.indexOf("null") === -1
                            ? project.files[0].src
                            : null
                        }
                        note={
                          project.notes.length > 0
                            ? project.notes[0].note
                            : null
                        }
                        name={
                          Array.isArray(project.submittedBy)
                            ? project.submittedBy[0].name
                            : project.submittedBy.name
                        }
                        description={project.description}
                        onStatusChange={(e) => onStatusChange(project._id, e)}
                        onShare={() => console.log("share")}
                        onNoteSave={(e) => {
                          if ((project.notes || []).length === 0) {
                            onNoteSave(project._id, e);
                          } else {
                            const noteId = project.notes[0]?._id || null;
                            if (noteId) {
                              onNoteEdit(project._id, noteId, e);
                            }
                          }
                        }}
                        onNoteDelete={() => {
                          const noteId = project.notes?.[0]?._id;
                          if (noteId) {
                            onNoteDelete(project._id, noteId);
                          }
                        }}
                        isNew={project.isNew}
                        onDownload={(e) => onDownload(e)}
                      />
                    </div>
                  ))}
                </>
              ) : (
                <div className={styles.loadMessage}>No Projects Found</div>
              )}
            </>
          ) : (
            <>
              {archiveStatus === "loading" ? (
                <div className={styles.loading}>
                  <Loader />
                </div>
              ) : archiveStatus === "error" ? (
                <div className={styles.loadMessage}>
                  Error loading archived projects. Please try again.
                </div>
              ) : organizedArchiveData &&
                Object.keys(organizedArchiveData).length > 0 ? (
                <>
                  {Object.keys(organizedArchiveData).map((date, index) => {
                    if (date.substring(0, 4) === year) {
                      return (
                        <div
                          className={styles.collapsible}
                          onClick={() => {
                            openIndex === index
                              ? setOpenIndex(null)
                              : setOpenIndex(index);
                          }}
                        >
                          <ProjectsPageCollapsible
                            isOpen={openIndex === index}
                            title={`${Object.keys(months).find((name) => {
                              if (months[name] === date.substring(5, 7)) {
                                return name;
                              }
                              return null;
                            })} ${date.substring(0, 4)}`}
                            children={
                              <div className={styles.projectItems}>
                                {Object.values(organizedArchiveData[date]).map(
                                  (project) => (
                                    <ProjectItem
                                      readOnly
                                      date={`${Object.keys(months).find(
                                        (name) => {
                                          if (
                                            months[name] ===
                                            project.submittedOn.substring(5, 7)
                                          ) {
                                            return name;
                                          }
                                          return null;
                                        }
                                      )} ${project.submittedOn.substring(
                                        8,
                                        10
                                      )}, ${project.submittedOn.substring(
                                        0,
                                        4
                                      )}`}
                                      status={project.status}
                                      img={
                                        project.files !== null &&
                                        project.files.length > 0 &&
                                        project.files[0].src.indexOf("null") ===
                                          -1
                                          ? project.files[0].src
                                          : null
                                      }
                                      note={
                                        project.notes.length > 0
                                          ? project.notes[0].note
                                          : null
                                      }
                                      name={
                                        Array.isArray(project.submittedBy)
                                          ? project.submittedBy[0].name
                                          : project.submittedBy.name
                                      }
                                      description={project.description}
                                      onNoteSave={(e) => {
                                        if (
                                          (project.notes || []).length === 0
                                        ) {
                                          onNoteSave(project._id, e);
                                        } else {
                                          const noteId =
                                            project.notes?.[0]?._id || null;
                                          if (noteId) {
                                            onNoteEdit(project._id, noteId, e);
                                          }
                                        }
                                      }}
                                      onNoteDelete={() => {
                                        const noteId = project.notes?.[0]?._id;
                                        if (noteId) {
                                          onNoteDelete(project._id, noteId);
                                        }
                                      }}
                                      isNew={project.isNew}
                                      onDownload={(e) => onDownload(e)}
                                    />
                                  )
                                )}
                              </div>
                            }
                          />
                        </div>
                      );
                    }
                    return null;
                  })}
                </>
              ) : (
                <div className={styles.loadMessage}>
                  No Archived Projects Found
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
